import { FC, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useChalkFlipFace } from "../../shared/component/ChalkBoardFlip/chalk-flip-context";
import { cn } from "../../shared/lib/cn";
import "./home-board.css";

const BOARD_SRC = "/brand/home-splash.mp4";
const BOARD_POSTER = "/brand/home-splash-poster.jpg";

function decodePath(pathname: string) {
  try {
    return decodeURIComponent(pathname);
  } catch {
    return pathname;
  }
}

function isTeamPath(pathname: string) {
  const path = decodePath(pathname);
  return path === "/команда" || path === "/aboutus";
}

function isContactsPath(pathname: string) {
  const path = decodePath(pathname);
  return path === "/контакты" || path === "/contacts";
}

function isSharedBoardPath(pathname: string) {
  const path = decodePath(pathname);
  return path === "/" || path === "/события" || path === "/events" || isTeamPath(pathname) || isContactsPath(pathname);
}

type HomeBoardVideoProps = {
  owner?: boolean;
};

export const HomeBoardVideo: FC<HomeBoardVideoProps> = ({ owner = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { pathname } = useLocation();
  const flip = useChalkFlipFace();
  const showVideo = isSharedBoardPath(pathname) && (owner || !flip.active);
  const shownRef = useRef(false);
  const [enterTeam, setEnterTeam] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !showVideo) {
      setPlaying(false);
      return;
    }
    const start = () => {
      video.muted = true;
      void video.play().catch(() => {});
    };
    start();
    video.addEventListener("canplay", start);
    window.addEventListener("pointerdown", start);
    return () => {
      video.removeEventListener("canplay", start);
      window.removeEventListener("pointerdown", start);
    };
  }, [showVideo]);

  useEffect(() => {
    const shouldFade = showVideo && !shownRef.current && (isTeamPath(pathname) || isContactsPath(pathname));
    if (!shouldFade) {
      shownRef.current = showVideo;
      return;
    }
    shownRef.current = true;
    setEnterTeam(true);
    const timer = window.setTimeout(() => setEnterTeam(false), 700);
    return () => {
      window.clearTimeout(timer);
      shownRef.current = false;
    };
  }, [pathname, showVideo]);

  if (!showVideo) return null;

  return (
    <>
      <span className="home-board-anchor" />
      {createPortal(
        <>
          <img className="home-board__poster" src={BOARD_POSTER} alt="" aria-hidden />
          <video
            ref={videoRef}
            className={cn(
              "home-board__video",
              playing && "is-playing",
              playing && enterTeam && "home-board__video--in",
            )}
            src={BOARD_SRC}
            poster={BOARD_POSTER}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden
            onPlaying={() => setPlaying(true)}
          />
        </>,
        document.body,
      )}
    </>
  );
};
